<template>
  <div>
    <div class="marketplace-checkout--table-header flex items-center justify-between flex-wrap gap-sm">
      <h2 class="text-xl fw-strong">
        <span v-if="authenticated && showAddressForm && !isUpdatingAddress">{{ 'New' | t }}</span>
        <span v-else-if="authenticated && showAddressForm && isUpdatingAddress">{{ 'Update' | t }}</span>
        {{ 'Shipping Address' | t }}
      </h2>
      <button
        v-if="authenticated && !showAddressForm"
        @click="showAddressForm = true"
        type="button"
        class="flex items-center justify-start gap-xs p-0 bg-transparent c-blue-400 hover:c-blue-700"
      >
        <SloIcon name="Plus" />
        {{ 'Add New Address' | t }}
      </button>
    </div>
    <SloLoader :loading="shippingAddressesLoading" :class="shippingAddressesLoading ? 'border-body' : ''">
      <div v-if="authenticated && !showAddressForm && shippingAddresses.length > 0">
        <div class="marketplace-checkout--table-body padding-full">
          <ShippingAddresses
            :shipping-addresses="shippingAddresses"
            :shipping-address-id="shippingAddressId"
            @selectAddress="handleSelectAddress"
            @updateAddress="setupUpdateAddressForm"
          />
        </div>
        <div class="flex items-end justify-end mt-lg">
          <SloButton @click="attachSelectedShippingAddress" :disabled="loading" class="fixed-button-width">
            <SloLoader :loading="loading" color="white" size="md">
              {{ 'Continue' | t }}
            </SloLoader>
          </SloButton>
        </div>
      </div>
      <form
        v-if="showAddressForm"
        @submit.prevent="handleAddressSubmit"
        action="#"
        class="marketplace-checkout--form-wrapper"
      >
        <div class="marketplace-checkout--table-body padding-full">
          <AddressFormFields
            :countries="shippingCountries"
            :regions="shippingCountryRegions"
            :original-address="address"
            :errors="addressErrors"
            :add-tool-tip="true"
            :tool-tip-message="'Shipping to US only' | t"
            @addressUpdate="handleAddressUpdate"
          />
          <SloField v-if="showSaveAddressToAccount" class="mt-md">
            <SloLabel for="save-address-to-account" class="flex flex-row items-center gap-sm m-0">
              <SloInput v-model="saveAddressToAccount" id="save-address-to-account" type="checkbox" value="0" class="m-0" />
              {{ 'Save this address to my account' | t }}
            </SloLabel>
          </SloField>
        </div>
        <div class="flex items-center justify-end gap-md mt-lg">
          <SloButton
            @click.prevent="handleCancel"
            :disabled="loading"
            type="button"
            fill="outline"
          >
            {{ authenticated ? 'Cancel' : 'Back' | t }}
          </SloButton>
          <SloButton type="submit" :disabled="loading" class="fixed-button-width">
            <SloLoader :loading="loading" color="white" size="md">
              {{ isUpdatingAddress ? 'Update' : 'Continue' | t }}
            </SloLoader>
          </SloButton>
        </div>
      </form>
    </SloLoader>
  </div>
</template>

<script>
import t from '../../main/i18n';
import {mapState, mapGetters, mapActions} from 'vuex';
import {marketplaceApi} from '../apis/marketplace';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloModal from '../../../lib/components/SloModal.vue';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import CartProduct from './CartProduct.vue';
import AddressFormFields from './AddressFormFields.vue';
import ShippingAddresses from './ShippingAddresses.vue';

export default {
  name: 'CheckoutStep2',
  components: {
    SloField,
    SloLabel,
    SloInput,
    SloButton,
    SloLoader,
    SloIcon,
    SloModal,
    SloProductImage,
    CartProduct,
    AddressFormFields,
    ShippingAddresses
  },
  filters: {
    t
  },
  props: {
    stepChange: {type: Boolean, default: false}
  },
  data() {
    return {
      loading: false,
      shippingAddressesLoading: true,
      shippingCountries: [],
      shippingCountryRegions: [],
      shippingAddressId: null,
      shippingAddresses: [],
      saveAddressToAccount: false,
      isUpdatingAddress: false,
      showAddressForm: false,
      addressErrors: [],
      address: {}
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    ...mapGetters('marketplace', ['cartHasRestrictedMessages']),
    ...mapState('marketplace', ['cartLoading', 'cartResponse']),
    showSaveAddressToAccount() {
      return this.authenticated && this.shippingAddresses.length > 0 && !this.isUpdatingAddress;
    },
    submitAddressAsDisposable() {
      return !this.authenticated || (!this.saveAddressToAccount && this.shippingAddresses.length > 0);
    }
  },
  async created() {
    this.resetAddress();
    this.shippingAddressesLoading = true;

    await marketplaceApi.getCountryCodes('SHIPPING')
      .then(res => {
        this.shippingCountries = res.data?.countries;
        this.address.country.isocode = 'US';
      })
      .catch(() => {
        this.shippingCountries = [];
        this.address.country.isocode = 'US';
      });

    if (this.authenticated) {
      await this.getShippingAddresses();
    } else {
      if (this.cartResponse?.deliveryAddress) {
        this.address = this.cartResponse.deliveryAddress;
      }
      this.showAddressForm = true;
    }

    this.shippingAddressesLoading = false;
  },
  watch: {
    async 'address.country.isocode'(newValue, oldValue) {
      if (newValue !== oldValue) {
        await this.getCountryRegions();
      }
    },
    showAddressForm(newValue) {
      if (!newValue) this.resetAddress();
    }
  },
  methods: {
    ...mapActions('marketplace', ['getCurrentCart']),
    async getShippingAddresses() {
      await marketplaceApi.getCurrentCartAddresses()
        .then(res => {
          this.shippingAddresses = res.data.addresses;
          if (this.shippingAddresses.length === 0) this.showAddressForm = true;
          this.shippingAddressId = this.cartResponse?.deliveryAddress
            ? this.cartResponse?.deliveryAddress?.id
            : this.shippingAddresses.find(address => address.defaultAddress)?.id;
        })
        .catch(() => {
          this.shippingAddressesLoading = false;
          this.$snotify.error(t('Error loading your shipping addresses, please try again'));
        });
    },
    resetAddress() {
      this.address = {
        firstName: '',
        attention: '',
        companyName: '',
        country: {isocode: 'US'},
        line1: '',
        line2: '',
        town: '',
        region: {isocode: '', isocodeShort: ''},
        postalCode: ''
      };
    },
    handleSelectAddress(addressId) {
      this.shippingAddressId = addressId;
    },
    async checkRestrictedMessages() {
      await this.getCurrentCart();

      if (this.cartHasRestrictedMessages) {
        this.$emit('restrictedMessagesModalToggle', true);
        this.loading = false;
        return false;
      }

      this.$emit('continue');
    },
    async attachSelectedShippingAddress() {
      this.loading = true;
      await marketplaceApi.selectCartShippingAddress(this.shippingAddressId)
        .then(() => this.checkRestrictedMessages())
        .catch(() => {
          this.loading = false;
          this.$snotify.error(t('Selecting shipping address failed, please try again'));
        });
    },
    async handleAddressSubmit() {
      if (!await this.validateAddress()) return;

      if (this.isUpdatingAddress) {
        await this.handleUpdateAddressSubmit();
      } else {
        await this.handleNewAddressSubmit();
      }
    },
    async handleNewAddressSubmit() {
      this.loading = true;
      await marketplaceApi.addCartShippingAddress({
        ...this.address,
        isDisposable: this.submitAddressAsDisposable}
      )
        .then(() => this.checkRestrictedMessages())
        .catch(() => {
          this.loading = false;
          this.$snotify.error(t('Error adding that shipping address'));
        });
    },
    async handleUpdateAddressSubmit() {
      try {
        this.loading = true;
        await marketplaceApi.updateCurrentUserCartAddress(this.address);
        await marketplaceApi.selectCartShippingAddress(this.address.id);
        await this.getCurrentCart();
        await this.getShippingAddresses();
        this.loading = false;
        this.isUpdatingAddress = false;
        this.showAddressForm = false;
        this.$snotify.success(t('Shipping address updated'));
      } catch (e) {
        this.loading = false;
        this.$snotify.error(t('Shipping address update failed'));
      }
      window.scrollTo(0, 0);
    },
    async validateAddress() {
      if (this.address.country.isocode !== 'US') return true;

      this.loading = true;
      this.addressErrors = [];
      return await marketplaceApi.validateAddressWithStrikeIron(this.address)
        .then(res => {
          if (res.data) {
            // Update address data with validated address from StrikeIron
            this.address.line1 = res.data.line1;
            this.address.line2 = res.data.line2;
            this.address.town = res.data.town;
            this.address.region.isocode = res.data.region.isocode;
            this.address.region.isocodeShort = res.data.region.isocodeShort;
            this.address.postalCode = res.data.postalCode;
            return true;
          } else {
            this.loading = false;
            this.$snotify.clear();
            this.$snotify.error(t('Invalid address provided'));
            window.scrollTo(0, 0);
            return false;
          }
        })
        .catch(e => {
          this.loading = false;
          this.addressErrors = e?.response?.data?.errors;
          this.$snotify.clear();
          this.$snotify.error(t('Invalid address provided'));
          window.scrollTo(0, 0);
          return false;
        });
    },
    async getCountryRegions() {
      this.address.region.isocode = '';
      this.address.region.isocodeShort = '';
      await marketplaceApi.getCountryRegions(this.address.country.isocode)
        .then(res => this.shippingCountryRegions = res.data?.regions)
        .catch(() => this.shippingCountryRegions = []);
    },
    async setupUpdateAddressForm(addressId) {
      this.shippingAddressesLoading = true;
      await marketplaceApi.getCurrentCartAddress(addressId)
        .then(res => {
          this.address = res.data;
          this.shippingAddressesLoading = false;
          this.showAddressForm = true;
          this.isUpdatingAddress = true;
        })
        .catch(() => {
          this.shippingAddressesLoading = false;
          this.$snotify.error(t('Error loading selected address'));
        });
    },
    handleCancel() {
      window.scrollTo(0, 0);
      this.isUpdatingAddress = false;
      if (this.authenticated) {
        this.resetAddress();
        this.showAddressForm = false;
        return;
      }
      this.$emit('continue', 'back');
    },
    handleAddressUpdate(address) {
      this.address = address;
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace-checkout {
  &--form-wrapper {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: start;
    width: 100%;

    .save-address--wrapper {
      &:focus-within {
        label {
          color: $color-gray-800;
        }
      }
    }
  }

  &--table-header {
    border: 1px solid $color-gray-400;
    border-radius: 1.2rem 1.2rem 0 0;
  }

  &--table-body {
    border-inline: 1px solid $color-gray-400;
    border-bottom: 1px solid $color-gray-400;
    border-radius: 0 0 1.2rem 1.2rem;
  }
}

:deep(.marketplace-checkout--shipping-addresses) {
  grid-template-columns: 1fr;
  margin: 0;

  @media (min-width: $screen-md) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.border-body {
  padding: 2.4rem;
  border-inline: 1px solid $color-gray-400;
  border-bottom: 1px solid $color-gray-400;
  border-radius: 0 0 1.2rem 1.2rem;
}

.fixed-button-width {
  min-width: 96px;
}
</style>
